import { projectConfigApi } from "@/api/projectConfigs";
import {
    projectApi,
    type Milestone,
    type Project,
    type ProjectId,
    type ProjectTeam,
    type ProjectUser,
    type ProjectVersion,
    type ProjectRoleHolders,
} from "@/api/projects";
import { useQueryCache } from "@pinia/colada";
import { ensureQueryData } from "./utils";

export const PROJECT_KEYS = {
    root: ["projects"] as const,
    list: () => [...PROJECT_KEYS.root, "list"] as const,
    users: (id: ProjectId) => [...PROJECT_KEYS.root, "users", id] as const,
    teams: (id: ProjectId) => [...PROJECT_KEYS.root, "teams", id] as const,
    versions: (id: ProjectId) => [...PROJECT_KEYS.root, "versions", id] as const,
    milestones: (id: ProjectId) => [...PROJECT_KEYS.root, "milestones", id] as const,
    configs: () => [...PROJECT_KEYS.root, "configs"] as const,
    labelConfigs: () => [...PROJECT_KEYS.root, "labelConfigs"] as const,
    priorityConfigs: () => [...PROJECT_KEYS.root, "priorityConfigs"] as const,
    resolutionConfigs: () => [...PROJECT_KEYS.root, "resolutionConfigs"] as const,
    statusConfigs: () => [...PROJECT_KEYS.root, "statusConfigs"] as const,
    typeConfigs: () => [...PROJECT_KEYS.root, "typeConfigs"] as const,
    customDataConfigs: () => [...PROJECT_KEYS.root, "customDataConfigs"] as const,
    roleConfigs: () => [...PROJECT_KEYS.root, "roleConfigs"] as const,
    roleHolders: (id: ProjectId) => [...PROJECT_KEYS.root, "roleHolders", id] as const,
};

const LOOKUP_STALE_MS = 5 * 60_000;

// Versions are the one lookup other people create mid-workflow, so a picker
// holding a five-minute-old list is missing options the user was just told about.
const VERSION_STALE_MS = 30_000;

export const projectsQueryOptions = {
    key: PROJECT_KEYS.list(),
    query: () => projectApi.getProjects(),
    staleTime: LOOKUP_STALE_MS,
};

export function projectUsersQueryOptions(id: ProjectId) {
    return {
        key: PROJECT_KEYS.users(id),
        query: () => projectApi.getUsers(id),
        staleTime: LOOKUP_STALE_MS,
    };
}

export function projectTeamsQueryOptions(id: ProjectId) {
    return {
        key: PROJECT_KEYS.teams(id),
        query: () => projectApi.getTeams(id),
        staleTime: LOOKUP_STALE_MS,
    };
}

export function projectVersionsQueryOptions(id: ProjectId) {
    return {
        key: PROJECT_KEYS.versions(id),
        query: () => projectApi.getVersions(id),
        staleTime: VERSION_STALE_MS,
    };
}

export function projectMilestonesQueryOptions(id: ProjectId) {
    return {
        key: PROJECT_KEYS.milestones(id),
        query: () => projectApi.getMilestones(id),
        staleTime: LOOKUP_STALE_MS,
    };
}

export const projectConfigsQueryOptions = {
    key: PROJECT_KEYS.configs(),
    query: () => projectConfigApi.getProjectConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export const labelConfigsQueryOptions = {
    key: PROJECT_KEYS.labelConfigs(),
    query: () => projectConfigApi.getLabelConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export const priorityConfigsQueryOptions = {
    key: PROJECT_KEYS.priorityConfigs(),
    query: () => projectConfigApi.getPriorityConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export const resolutionConfigsQueryOptions = {
    key: PROJECT_KEYS.resolutionConfigs(),
    query: () => projectConfigApi.getResolutionConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export const roleConfigsQueryOptions = {
    key: PROJECT_KEYS.roleConfigs(),
    query: () => projectConfigApi.getRoleConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export function projectRoleHoldersQueryOptions(id: ProjectId) {
    return {
        key: PROJECT_KEYS.roleHolders(id),
        query: () => projectApi.getRoleHolders(id),
        staleTime: LOOKUP_STALE_MS,
    };
}

export const statusConfigsQueryOptions = {
    key: PROJECT_KEYS.statusConfigs(),
    query: () => projectConfigApi.getStatusConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export const typeConfigsQueryOptions = {
    key: PROJECT_KEYS.typeConfigs(),
    query: () => projectConfigApi.getTypeConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export const customDataConfigsQueryOptions = {
    key: PROJECT_KEYS.customDataConfigs(),
    query: () => projectConfigApi.getCustomDataConfigurations(),
    staleTime: LOOKUP_STALE_MS,
};

export function readCachedProjects(): Project[] | undefined {
    return useQueryCache().getQueryData<Project[]>(PROJECT_KEYS.list());
}

// Reactive view of every fetched per-project collection of one kind, keyed by
// project id. Reading it inside a getter tracks both entry data and creation.
export function readCachedByProject<T>(
    kind: "users" | "teams" | "versions" | "milestones" | "roleHolders"
): Map<ProjectId, T> {
    const map = new Map<ProjectId, T>();
    for (const entry of useQueryCache().getEntries({ key: [...PROJECT_KEYS.root, kind] })) {
        const data = entry.state.value.data as T | undefined;
        const id = entry.key[2] as ProjectId | undefined;
        if (data !== undefined && id !== undefined) {
            map.set(id, data);
        }
    }
    return map;
}

export function readCachedProjectUsers(): Map<ProjectId, ProjectUser[]> {
    return readCachedByProject<ProjectUser[]>("users");
}

export function readCachedProjectTeams(): Map<ProjectId, ProjectTeam[]> {
    return readCachedByProject<ProjectTeam[]>("teams");
}

export function readCachedProjectVersions(): Map<ProjectId, ProjectVersion[]> {
    return readCachedByProject<ProjectVersion[]>("versions");
}

export function readCachedProjectRoleHolders(): Map<ProjectId, ProjectRoleHolders[]> {
    return readCachedByProject<ProjectRoleHolders[]>("roleHolders");
}

export function readCachedProjectMilestones(): Map<ProjectId, Milestone[]> {
    return readCachedByProject<Milestone[]>("milestones");
}

export function readCachedConfigList<T>(key: readonly string[]): T[] | undefined {
    return useQueryCache().getQueryData<T[]>(key);
}

export async function ensureProjects(force = false): Promise<Project[] | undefined> {
    return await ensureQueryData<Project[]>(projectsQueryOptions, force);
}
